import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import api from "../../services/api";
import { CONTRACTS } from "./salary/constants";
import { PERIOD } from "../../constants";
import { getCostSalary, getCostExpenses, getMonthlyTotalCost } from "./salary/salary";

const EMPTY_FORM = {
  start_date: new Date().toISOString().substring(0, 10),
  end_date: "",
  contract_type: "",
  payment_type: "MONTHLY",
  payment_currency: "EURO",
  payment_value: "",
  brut_salary: "",
  days_worked: "",
  tjms: "",
  organisation_name: "",
  comment: "",
};

// Compute the derived cost fields the same way the Payment tab does.
// The user doc is merged in first: school_cost, french_ticketresto, french_alan and office
// live on the person, not on the period form, and the cost breakdown reads them. Without
// them school_cost is undefined, which turns the whole salary total into NaN and stores tjm: 0.
function computeCosts(form, user) {
  const values = { ...user, ...form, contract: form.contract_type };
  let tjm_salary = 0;
  let tjm_expense = 0;
  let total_monthly_cost;

  if (form.payment_type === "DAILY" && form.contract_type === CONTRACTS.FREELANCE) {
    tjm_salary = Number(form.payment_value) || 0;
    tjm_expense = 0;
  } else if (form.payment_type === "MONTHLY" && form.contract_type === CONTRACTS.FREELANCE) {
    tjm_salary = form.days_worked ? Math.round(Number(form.payment_value) / Number(form.days_worked)) : 0;
    tjm_expense = 0;
    total_monthly_cost = Number(form.payment_value) || 0;
  } else {
    tjm_salary = getCostSalary(values);
    tjm_expense = getCostExpenses(values);
    total_monthly_cost = getMonthlyTotalCost(values);
  }

  const tjm = (parseFloat(tjm_salary) || 0) + (parseFloat(tjm_expense) || 0);
  return { tjm_salary, tjm_expense, tjm, total_monthly_cost };
}

export default function Contracts({ user, setUser }) {
  const [contracts, setContracts] = useState([]);
  const [organisations, setOrganisations] = useState([]);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [editingId, setEditingId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const fetchContracts = async () => {
    const { ok, data } = await api.post("/user_contract/search", { user_id: user._id });
    if (ok) setContracts(data);
  };

  useEffect(() => {
    fetchContracts();
    api.get("/organisation").then((res) => {
      if (res.ok) setOrganisations(res.data);
    });
  }, [user._id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setForm({ ...EMPTY_FORM });
    setEditingId(null);
  };

  const startEdit = (c) => {
    setEditingId(c._id);
    setForm({
      start_date: c.start_date ? new Date(c.start_date).toISOString().substring(0, 10) : "",
      end_date: c.end_date ? new Date(c.end_date).toISOString().substring(0, 10) : "",
      contract_type: c.contract_type || "",
      payment_type: c.payment_type || "MONTHLY",
      payment_currency: c.payment_currency || "EURO",
      payment_value: c.payment_value ?? "",
      brut_salary: c.brut_salary ?? "",
      days_worked: c.days_worked ?? "",
      tjms: c.tjms ?? "",
      organisation_name: c.organisation_name || "",
      comment: c.comment || "",
    });
  };

  const refreshUser = async () => {
    const { ok, data } = await api.get(`/user/${user._id}`);
    if (ok && data) setUser(data);
  };

  const save = async () => {
    if (!form.start_date) return toast.error("Start date is required");
    if (!form.payment_value) return toast.error("Payment amount is required");
    if (form.end_date && form.end_date < form.start_date) return toast.error("End date must be after start date");

    try {
      setIsLoading(true);
      const org = organisations.find((o) => o.name === form.organisation_name);
      const payload = {
        user_id: user._id,
        user_name: user.name,
        start_date: form.start_date,
        end_date: form.end_date || null,
        contract_type: form.contract_type,
        payment_type: form.payment_type,
        payment_currency: form.payment_currency,
        payment_value: Number(form.payment_value),
        brut_salary: form.brut_salary ? Number(form.brut_salary) : undefined,
        days_worked: form.days_worked ? Number(form.days_worked) : undefined,
        tjms: form.tjms ? Number(form.tjms) : undefined,
        organisation_name: form.organisation_name,
        organisation_id: org?._id,
        business_unit_id: user.business_unit_id,
        business_unit_name: user.business_unit_name,
        comment: form.comment,
        ...computeCosts(form, user),
      };

      if (editingId) {
        await api.put(`/user_contract/${editingId}`, payload);
      } else {
        await api.post("/user_contract", payload);
      }
      toast.success("Saved!");
      resetForm();
      await fetchContracts();
      await refreshUser();
    } catch (error) {
      console.error("Error saving contract period:", error);
      toast.error("Failed to save. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Are you sure ?")) return;
    try {
      await api.remove(`/user_contract/${id}`);
      toast.success("Deleted!");
      await fetchContracts();
      await refreshUser();
    } catch (error) {
      console.error("Error deleting contract period:", error);
      toast.error("Failed to delete. Please try again.");
    }
  };

  const now = new Date();
  // Active = newest-started period that has begun and has not ended (list is sorted -start_date).
  const activeId = contracts.find((c) => new Date(c.start_date) <= now && (!c.end_date || new Date(c.end_date) >= now))?._id;

  // Displayed end: the stored end_date, else derived from the next period's start (day before).
  // contracts is sorted newest-first, so the successor of contracts[i] is contracts[i - 1].
  const displayedEnd = (c, i) => {
    if (c.end_date) return new Date(c.end_date).toLocaleDateString();
    const next = contracts[i - 1];
    if (!next) return "—";
    const d = new Date(next.start_date);
    d.setDate(d.getDate() - 1);
    return `${d.toLocaleDateString()} (auto)`;
  };

  return (
    <div className="w-full space-y-6">
      <div>
        <label className="font-bold text-lg">Contract periods</label>
        <p className="text-sm text-gray-500">
          Each period defines the pay terms from its start date onward. A raise = add a new period. The most recent period that has started and not ended is the active one. Leave the end date empty for open-ended periods (the next period's start closes it automatically); set one to make a fixed-term period that just stops.
        </p>
      </div>

      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-600">
            <tr>
              <th className="p-2 text-left">Start</th>
              <th className="p-2 text-left">End</th>
              <th className="p-2 text-left">Contract</th>
              <th className="p-2 text-left">Type</th>
              <th className="p-2 text-right">Amount</th>
              <th className="p-2 text-right">Monthly cost</th>
              <th className="p-2 text-right">TJM</th>
              <th className="p-2"></th>
            </tr>
          </thead>
          <tbody>
            {contracts.length === 0 && (
              <tr>
                <td colSpan={8} className="p-4 text-center text-gray-400">
                  No period yet
                </td>
              </tr>
            )}
            {contracts.map((c, i) => (
              <tr key={c._id} className={`border-t border-gray-100 ${c._id === activeId ? "bg-green-50" : ""}`}>
                <td className="p-2">
                  {new Date(c.start_date).toLocaleDateString()}
                  {c._id === activeId && <span className="ml-2 text-xs text-green-700 font-medium">active</span>}
                </td>
                <td className="p-2 text-gray-600">{displayedEnd(c, i)}</td>
                <td className="p-2">{c.contract_type || "-"}</td>
                <td className="p-2">{c.payment_type || "-"}</td>
                <td className="p-2 text-right">{c.payment_value != null ? `${c.payment_value} ${c.payment_currency || ""}` : "-"}</td>
                <td className="p-2 text-right">{c.total_monthly_cost ?? "-"}</td>
                <td className="p-2 text-right">{c.tjm ?? "-"}</td>
                <td className="p-2 text-right whitespace-nowrap">
                  <button className="text-blue-600 hover:underline mr-3" onClick={() => startEdit(c)}>
                    Edit
                  </button>
                  <button className="text-red-600 hover:underline" onClick={() => remove(c._id)}>
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="p-6 border border-gray-200 rounded-lg shadow-sm bg-white space-y-4">
        <div className="flex justify-between items-center">
          <label className="font-bold">{editingId ? "Edit period" : "New period / Raise"}</label>
          {editingId && (
            <button className="text-sm text-gray-500 hover:underline" onClick={resetForm}>
              Cancel edit
            </button>
          )}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <Field label="Start date">
            <input type="date" name="start_date" value={form.start_date} onChange={handleChange} className={inputCls} />
          </Field>
          <Field label="End date (optional)">
            <input type="date" name="end_date" value={form.end_date} onChange={handleChange} className={inputCls} />
          </Field>
          <Field label="Contract type">
            <select name="contract_type" value={form.contract_type} onChange={handleChange} className={inputCls}>
              <option value="">Select</option>
              {Object.keys(CONTRACTS).map((e) => (
                <option key={e} value={e}>
                  {e}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Organisation">
            <select name="organisation_name" value={form.organisation_name} onChange={handleChange} className={inputCls}>
              <option value="">Select</option>
              {organisations
                .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
                .map((org) => (
                  <option key={org._id} value={org.name}>
                    {org.name}
                  </option>
                ))}
            </select>
          </Field>
          <Field label="Payment type">
            <select name="payment_type" value={form.payment_type} onChange={handleChange} className={inputCls}>
              {Object.keys(PERIOD).map((e) => (
                <option key={e} value={e}>
                  {e}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Payment amount">
            <input type="number" name="payment_value" value={form.payment_value} onChange={handleChange} className={inputCls} placeholder="e.g. 2000" />
          </Field>
          <Field label="Currency">
            <select name="payment_currency" value={form.payment_currency} onChange={handleChange} className={inputCls}>
              <option value="EURO">EURO</option>
              <option value="DOLLAR">DOLLAR</option>
            </select>
          </Field>
          <Field label="Annual gross salary">
            <input type="number" name="brut_salary" value={form.brut_salary} onChange={handleChange} className={inputCls} />
          </Field>
          <Field label="Working days">
            <input type="number" name="days_worked" value={form.days_worked} onChange={handleChange} className={inputCls} />
          </Field>
          <Field label="Sales rate (TJMS)">
            <input type="number" name="tjms" value={form.tjms} onChange={handleChange} className={inputCls} />
          </Field>
        </div>

        <Field label="Comment">
          <input type="text" name="comment" value={form.comment} onChange={handleChange} className={inputCls} placeholder="Optional" />
        </Field>

        <div className="flex justify-end">
          <button
            className={`px-4 py-2 rounded-md text-white transition-colors ${isLoading ? "bg-gray-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"}`}
            onClick={save}
            disabled={isLoading}>
            {isLoading ? "Saving..." : editingId ? "Update period" : "Add period"}
          </button>
        </div>
      </div>
    </div>
  );
}

const inputCls = "w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-sm p-2";

const Field = ({ label, children }) => (
  <div>
    <div className="text-sm text-gray-700 font-medium mb-1">{label}</div>
    {children}
  </div>
);
